// auth.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { SnackbarService } from './services/snackbar.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService, 
    private snackBarService: SnackbarService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !request.headers.has('X-Retry')) {
          // Convert the Promise from refreshToken to an Observable
          return from(this.authService.refreshToken()).pipe(
            switchMap(() => {
              // Retrieve the new token from local storage or a service method
              const token = localStorage.getItem('legalresources-access-token');
              const authReq = request.clone({
                setHeaders: { Authorization: `Bearer ${token}` },
                headers: request.headers.set('X-Retry', 'true') // Mark this request as a retry
              });
              return next.handle(authReq); // Retry with the new token
            }),
            catchError(refreshError => {
              // If refreshing the token fails, handle it here
              this.authService.login();
              return throwError(refreshError);
            })
          );
        }
        else if (error.status === 401 && request.headers.has('X-Retry')) {
          // If the request has already been retried, redirect to the login page
          this.snackBarService.open('Your session has expired. Please log in again.', "warning");
          this.authService.login();
        } else if (!request.headers.has('X-Skip-Error-Snackbar')) {
          this.handleHttpError(error);
        }
  
        // Re-throw with error.error as a readable string
        return throwError(() => new HttpErrorResponse({
          error: this.extractErrorMessage(error),
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined
        }));
      })
    );
  }

  private handleHttpError(error: HttpErrorResponse) {
    const message = this.extractErrorMessage(error);
  
    switch (error.status) {
      case 400:
        this.snackBarService.open(message, "error");
        break;
      case 403:
        this.snackBarService.open('You are not authorized to access this resource', "warning");
        break;
      case 422:
        this.snackBarService.open(message, "error");
        break;
      case 500:
        this.snackBarService.open('An internal server error occurred. Please contact support.', "error");
        break;
      default:
        this.snackBarService.open('An unexpected error occurred. Please try again.', "error");
        break;
    }
  }
  
  private extractErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
  
    if (typeof body === 'string' && body.trim().length > 0 && this.isUserFriendly(body)) {
      return body;
    }
  
    if (body?.errors && typeof body.errors === 'object') {
      const fields = Object.keys(body.errors)
        .map(field => this.camelToLabel(field))
        .join(', ');
      return `Required fields missing: ${fields}`;
    }
  
    if (body?.title && this.isUserFriendly(body.title)) return body.title;
    if (body?.message && this.isUserFriendly(body.message)) return body.message;
  
    return 'An unexpected error occurred. Please try again or contact support.';
  }

  private isUserFriendly(text: string): boolean {
    const htmlPattern = /<!DOCTYPE|<html|<div|<span/i;
    const stackTracePattern = /at .+Exception|stack trace/i;

    if (htmlPattern.test(text)) return false;
    if (stackTracePattern.test(text)) return false;

    return text.length <= 300;
  }
  
  private camelToLabel(field: string): string {
    return field
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/^./, c => c.toUpperCase());
  }
}
